// Editor for `page.chrome` — the small page-wide affordances that float
// above the content (back-to-top, reading progress) plus two page-level
// behaviour switches (smooth anchor scrolling, hero-image preload).
//
// Rendered by PageChromeRenderer (page-runtime marketingRenderers.tsx) and
// the static-site renderer (site-ssr/renderSite.tsx: `pg-smooth` class and
// heroPreload). Every field is optional; blank / unchecked stores
// `undefined` so the runtime default applies, and each placeholder names
// that default.

import type { PageChrome } from './types'
import { ColorPickerField } from './ColorPickerField'
import { OptionSelect, numOrUndef } from './settingFields'
import { TerminalEditor } from './LiveEditors'

interface Props {
  value: PageChrome | undefined
  onChange: (next: PageChrome) => void
}

export function PageChromeEditor({ value, onChange }: Props) {
  const c = value ?? {}
  const set = (patch: Partial<PageChrome>) => onChange({ ...c, ...patch })

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      {/* ── Back to top ── */}
      <div style={groupStyle}>
        <label style={checkRow}>
          <input type="checkbox" checked={!!c.backToTop} onChange={e => set({ backToTop: e.target.checked || undefined })} style={{ marginTop: 2 }} />
          <span>
            <strong>Back-to-top button</strong>
            <div style={hint}>A floating button that appears once the visitor has scrolled down.</div>
          </span>
        </label>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, opacity: c.backToTop ? 1 : 0.5 }}>
          <div>
            <label style={fieldLabel}>Show after (px scrolled)</label>
            <input
              type="number" min={0} max={10000} step={50}
              value={c.backToTopAfter ?? ''}
              placeholder="600"
              disabled={!c.backToTop}
              onChange={e => set({ backToTopAfter: numOrUndef(e.target.value) })}
              style={inputStyle}
            />
          </div>
          <div>
            <label style={fieldLabel}>Position</label>
            <OptionSelect
              value={c.backToTopPosition}
              options={[
                { value: 'bottom-right', label: 'Bottom right' },
                { value: 'bottom-left', label: 'Bottom left' },
              ]}
              unsetLabel="Default · bottom right"
              disabled={!c.backToTop}
              onChange={v => set({ backToTopPosition: v as PageChrome['backToTopPosition'] })}
              style={inputStyle}
            />
          </div>
        </div>
      </div>

      {/* ── Reading progress ── */}
      <div style={groupStyle}>
        <label style={checkRow}>
          <input type="checkbox" checked={!!c.scrollProgress} onChange={e => set({ scrollProgress: e.target.checked || undefined })} style={{ marginTop: 2 }} />
          <span>
            <strong>Reading progress bar</strong>
            <div style={hint}>A thin bar pinned to the top of the window that fills as the visitor scrolls.</div>
          </span>
        </label>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, opacity: c.scrollProgress ? 1 : 0.5 }}>
          <ColorPickerField
            label="Bar color"
            value={c.scrollProgressColor}
            placeholder="Primary color (default)"
            onChange={v => set({ scrollProgressColor: v.trim() || undefined })}
          />
          <div>
            <label style={fieldLabel}>Height (px)</label>
            <input
              type="number" min={1} max={20}
              value={c.scrollProgressHeight ?? ''}
              placeholder="3"
              disabled={!c.scrollProgress}
              onChange={e => set({ scrollProgressHeight: numOrUndef(e.target.value) })}
              style={{ ...inputStyle, marginTop: 6 }}
            />
          </div>
        </div>
      </div>

      {/* ── Behaviour switches ── */}
      <div style={groupStyle}>
        <label style={checkRow}>
          <input type="checkbox" checked={!!c.smoothScroll} onChange={e => set({ smoothScroll: e.target.checked || undefined })} style={{ marginTop: 2 }} />
          <span>
            <strong>Smooth scrolling for in-page links</strong>
            <div style={hint}>Anchor links glide instead of jumping. Skipped automatically for visitors who prefer reduced motion.</div>
          </span>
        </label>
        {/* Default ON (the renderer preloads unless this is exactly false),
            so checking it again clears the key rather than storing `true`. */}
        <label style={checkRow}>
          <input
            type="checkbox"
            checked={c.preloadHeroImage !== false}
            onChange={e => set({ preloadHeroImage: e.target.checked ? undefined : false })}
            style={{ marginTop: 2 }}
          />
          <span>
            <strong>Preload the hero image</strong>
            <div style={hint}>Fetches the first hero / priority image early so the page paints faster on the published site. On by default.</div>
          </span>
        </label>
      </div>

      {/* ── Terminal (kiosk) mode ── */}
      <div style={groupStyle}>
        <strong style={{ fontSize: 14 }}>Terminal mode</strong>
        <TerminalEditor value={c.terminal} onChange={(terminal) => set({ terminal })} />
      </div>
    </div>
  )
}

const groupStyle: React.CSSProperties = {
  display: 'grid', gap: 10, padding: 12,
  border: '1px solid var(--color-border)', borderRadius: 6,
}
const checkRow: React.CSSProperties = {
  display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 14, cursor: 'pointer',
}
const hint: React.CSSProperties = { fontSize: 13, color: 'var(--color-text-secondary)' }
const fieldLabel: React.CSSProperties = { fontSize: 13, color: 'var(--color-text-secondary)' }
const inputStyle: React.CSSProperties = {
  width: '100%', padding: '6px 8px', fontSize: 14,
  border: '1px solid var(--color-border-strong)', borderRadius: 4,
  background: 'var(--color-input-bg)', color: 'var(--color-text)',
}
